import clsx from "clsx";
import { Wrench } from "lucide-react";

import type { CapopsComponent, MaintenancePhase, UnitCapops as Capops } from "../api/types";
import { ProgressBar } from "../components/Gauge";
import { CardSection } from "../components/panel";
import { Motion } from "../components/presence";
import { Button } from "../components/ui";
import { k, t } from "../i18n/i18n";
import {
  CAPOPS_FULL,
  CAPOPS_START_MAX,
  capopsSentence,
  colorTone,
  dtoColor,
  dtoReading,
  familyDto,
  familyLabel,
  limitText,
  maintenanceColor,
  percentText,
  unitCapops,
} from "../maintenance";
import { useUi } from "../state/ui";
import { useLogistics } from "../views/logistics/LogisticsPanel";
import { CapopsStatusIcon, DtoStatusIcon, TONE_TEXT as DTO_TONE } from "../views/maintenance/DtoCurve";

/** CAPOPS de l'unité sur sa fiche (chantier « DTO / CAPOPS ») : valeur au temps affiché, couleur avec son icône
 *  (seuils de l'iPad), ce qui la limite (`capopsSentence`) et les trois composantes (personnel, matériels,
 *  munitions) ; le maillon le plus faible est en gras (aucun quand le plafond de départ, 90 % en T0, la limite). À part, la DTO de l'unité dans sa famille, avec sa propre
 *  lecture face à la DTO requise de son parc au temps (« conforme à la DTO requise » / « sous la DTO requise ») :
 *  indicateur logistique, jamais aux couleurs de la CAPOPS. Lue dans la réponse de la logistique, par la même requête que les vues
 *  Logistique et Maintenance (`useLogistics`). Rien en CENTAC (`maintenance` nul : la CAPOPS vient des résultats de
 *  la rotation) ni pour une unité absente de `units` (ennemi, unité non posée). */

const COMPONENTS: { key: Exclude<CapopsComponent, "depart">; label: string }[] = [
  { key: "personnel", label: k("Personnel") },
  { key: "materiel", label: k("Matériels") },
  { key: "munitions", label: k("Munitions") },
];

const TONE_TEXT = { ok: "text-ok", warn: "text-warn", danger: "text-danger", neutral: "text-ink" } as const;

/** Ton de la barre d'une composante (couleur de son pourcentage). */
function barTone(value: number): "ok" | "warn" | "danger" {
  const tone = colorTone(maintenanceColor(value));
  return tone === "neutral" ? "danger" : tone;
}

/** Contenu de la section, sans la requête (essais). La DTO requise vient de la CAPOPS (`capops.required`). */
export function CapopsBody({
  capops,
  phase,
  onOpen,
}: {
  capops: Capops;
  phase: MaintenancePhase;
  onOpen?: () => void;
}) {
  const tone = colorTone(capops.color);
  const limit = limitText(capops);
  const dto = capops.dto;
  return (
    <>
      <div className="flex items-center gap-2">
        <CapopsStatusIcon percent={capops.capops} size={16} />
        <span className={clsx("text-stat leading-tight font-semibold tabular-nums", TONE_TEXT[tone])}>
          {percentText(capops.capops)}
        </span>
        <span className="min-w-0 text-meta leading-snug text-ink-soft">
          {limit ? limit.charAt(0).toUpperCase() + limit.slice(1) : t("Rien ne la limite")}
        </span>
      </div>
      <ul className="mt-2 space-y-1.5" aria-label={capopsSentence(capops)}>
        {COMPONENTS.map(({ key, label }) => {
          const value = capops[key];
          // Hélicoptères : hors des familles de matériel, la composante « matériels » n'existe pas.
          if (value === null) return null;
          const limiting = capops.limit === key;
          const detail = key === "materiel" && capops.family ? familyLabel(capops.family) : null;
          return (
            <li key={key} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
              <span className={clsx("text-meta leading-snug", limiting ? "font-semibold text-ink" : "text-ink-soft")}>
                {t(label)}
                {detail && <span className="font-normal text-ink-muted"> · {detail}</span>}
              </span>
              <span className={clsx("text-right text-meta tabular-nums", limiting ? "font-semibold text-ink" : "text-ink-soft")}>
                {percentText(value)}
              </span>
              <ProgressBar value={value / 100} tone={barTone(value)} label={t(label)} className="col-span-2" />
            </li>
          );
        })}
      </ul>
      <p className="pt-2 text-meta leading-snug text-ink-muted">
        {t("Au temps {phase}, la CAPOPS est la plus faible des trois composantes, en % du potentiel initial, rapportée à la CAPOPS de départ ({n} % au plus en T0).", { phase, n: CAPOPS_START_MAX })}
      </p>
      {dto !== null && capops.family && capops.required != null && (
        <div className="mt-3 border-t border-line pt-2.5">
          {/* Définition en info-bulle, comme dans la vue CAPOPS / DTO : pas de paragraphe de plus sur la fiche. */}
          <div
            className="flex items-center gap-2"
            title={t("Cette DTO chute avec les avaries et les dommages de combat et remonte avec les remises en état ; la composante Matériels est cette DTO rapportée à celle de T0.")}
          >
            <DtoStatusIcon percent={dto} required={capops.required} />
            <span className="min-w-0 flex-1 text-meta leading-snug text-ink-soft">
              {t("{dto} en {phase}", { dto: familyDto(capops.family), phase })}
            </span>
            <span className={clsx("text-meta font-semibold tabular-nums", DTO_TONE[colorTone(dtoColor(dto, capops.required))])}>
              {percentText(dto)}
            </span>
          </div>
          <p className="mt-0.5 text-meta leading-snug text-ink-muted">
            {t("{reading} (requise {n} %)", { reading: dtoReading(dto, capops.required), n: Math.round(capops.required) })}
          </p>
        </div>
      )}
      {onOpen && (
        <div className="pt-2">
          <Button variant="secondary" size="sm" onClick={onOpen}>
            <Wrench size={14} /> {t("Ouvrir la maintenance")}
          </Button>
        </div>
      )}
    </>
  );
}

/** Fenêtre sous 1 024 px (`TABLET_QUERY` de la barre latérale) : la fiche d'objet y masque la colonne gauche. */
const NARROW_QUERY = "(max-width: 1023.98px)";

/** Ouvrir la maintenance depuis la fiche la ferme-t-il ? Oui en fenêtre étroite seulement ; en largeur ordinateur,
 *  la fiche reste ouverte à côté de la vue. */
export function closesCardOnOpen(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW_QUERY).matches;
}

export function UnitCapops({ exerciseId, unitKey, phase }: { exerciseId: string; unitKey: string; phase: MaintenancePhase }) {
  const { query } = useLogistics(exerciseId, phase);
  const focusMaintenance = useUi((s) => s.focusMaintenance);
  const capops = unitCapops(query.data, unitKey, phase);
  if (!capops) return null;
  // La vue Maintenance s'ouvre sur la ligne de l'unité, désignée dans le tableau « CAPOPS des unités ». Fenêtre
  // étroite : la fiche masque la colonne gauche (`leftColumnLayout`), elle se ferme pour laisser voir la vue.
  const open = () => focusMaintenance(null, unitKey, closesCardOnOpen());
  return (
    <Motion origin="fade">
      <CardSection title={t(CAPOPS_FULL)}>
        <CapopsBody capops={capops} phase={phase} onOpen={open} />
      </CardSection>
    </Motion>
  );
}
